// @flow

import React, { type Element, useState } from 'react'
import { core } from '../../../lib/WasmCache'
import {
  IconButton,
  FormControl,
  InputLabel,
  InputAdornment,
  OutlinedInput,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@material-ui/core'
import { useValidationResult, handleBlur, handleChange } from '../../../lib/CoreValidations'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { VisibilityOff, Visibility, DoneOutlined, ErrorOutlineOutlined } from '@material-ui/icons'

import type { ValidationCriterion } from '@bil0-dr-bill/dr-bill-core'
import { green } from '../../Common/constants'
import { grey } from '@material-ui/core/colors'

type Props = {
  value: string,
  setValue: (string) => void,
  setIsValid: (boolean) => void,
}

const useStyles = makeStyles((theme) =>
  createStyles({
    listItem: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    ListItemIcon: {
      alignSelf: 'flex-start',
      paddingTop: 6,
    },
    ListItemIconSuccess: {
      alignSelf: 'flex-start',
      paddingTop: 6,
      color: green,
    },
  })
)

function HelperText() {
  return <FormHelperText variant="standard">Your password must:</FormHelperText>
}

function ValidationCriterionTag({ result }: { result: ValidationCriterion }) {
  const classes = useStyles()

  return (
    <ListItem disableGutters className={classes.listItem}>
      <ListItemIcon
        className={result.is_success ? classes.ListItemIconSuccess : classes.ListItemIcon}
      >
        {result.is_success ? <DoneOutlined /> : <ErrorOutlineOutlined />}
      </ListItemIcon>
      <Box color={result.is_success ? green : grey[600]}>
        <ListItemText primary={result.error_message} />
      </Box>
    </ListItem>
  )
}
function PasswordValidationResultComponent({ results }: { results: Array<ValidationCriterion> }) {
  if (results.length === 0) {
    return ''
  }

  return (
    <List component="nav" aria-labelledby="nested-list-subheader">
      {results.map((res) => (
        <ValidationCriterionTag result={res} key={res.error_message} />
      ))}
    </List>
  )
}

function PasswordField({ value, setValue, setIsValid }: Props): Element<'div'> {
  const [validationResult, onChange, onBlur] = useValidationResult(core.validatePassword)
  const [isShowPassword, setShowPassword] = useState(false)
  const [isChanging, setIsChanging] = useState(false)

  const handleClickShowPassword = () => {
    setShowPassword((currentValue) => !currentValue)
  }

  const handleMouseDownPassword = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  return (
    <div>
      <FormControl variant="outlined" fullWidth>
        <InputLabel
          htmlFor="password"
          variant="outlined"
          required
          error={!(validationResult.is_success || isChanging)}
        >
          Password
        </InputLabel>
        <OutlinedInput
          id="password"
          type={isShowPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => {
            setIsChanging(true)
            handleChange(e.target.value, onChange, setIsValid, setValue)
          }}
          onBlur={(e) => {
            setIsChanging(false)
            handleBlur(e.target.value, onBlur, setIsValid)
          }}
          fullWidth
          required
          error={!(validationResult.is_success || isChanging)}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
              >
                {isShowPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          }
          label="Password"
        />

        {validationResult.results.length !== 0 && <HelperText />}
      </FormControl>
      <PasswordValidationResultComponent results={validationResult.results} />
    </div>
  )
}

export default PasswordField
